import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getProductBySlug } from './fourthwall';
import { Plus, Minus } from 'lucide-react';
import { QuantitySelector } from './components/CustomUI';
import { useShop } from './context/ShopContext';
import { AnimatedPrice } from './components/AnimatedPrice';
// 👇 IMPORT SIZE GUIDE
import { SizeGuide } from './components/SizeGuide';

export default function ProductPage() { 
  const { slug } = useParams();
  const { currency, getProductFromCache, addToCart } = useShop();
  
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState('');
  const [openSection, setOpenSection] = useState(null);
  
  // 👇 NEW STATE FOR MODAL
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  useEffect(() => {
    const cachedData = getProductFromCache(slug);
    if (cachedData) {
      setProduct(cachedData);
      setMainImage(cachedData.images[0]?.url);
      if (cachedData.variants && cachedData.variants.length > 0) {
        setSelectedVariant(cachedData.variants[0]);
      }
    }
    window.scrollTo(0,0);
  }, [slug, getProductFromCache]);

  if (!product) return <div className="loading-screen"><div className="loader-shape"></div></div>;

  const cleanVariantName = (variant) => {
    if (variant.attributes?.size?.name) return variant.attributes.size.name;
    return variant.name.split(',').pop().trim();
  };

  const getPrice = () => {
    if (!selectedVariant || !selectedVariant.prices) return { value: '0.00', currency: 'USD' };
    return selectedVariant.prices[currency] || selectedVariant.prices['USD'];
  };

  const currentPrice = getPrice();

  const handleAdd = () => {
    if (!selectedVariant) return;
    addToCart({
      id: product.id,
      name: product.name,
      price: currentPrice.value,
      currency: currentPrice.currency,
      image: product.images[0]?.url,
      variantId: selectedVariant.id,
      variantName: cleanVariantName(selectedVariant)
    }, quantity);
  };

  const toggle = (title) => setOpenSection(openSection === title ? null : title);

  return (
    <div className="product-page-container">
      {/* 👇 SIZE GUIDE MODAL */}
      <SizeGuide 
        isOpen={isSizeGuideOpen} 
        onClose={() => setIsSizeGuideOpen(false)} 
        productTitle={product.name} 
      />

      <div className="product-layout">
        <div className="product-gallery">
          <div className="thumbnail-list">
            {product.images.map((img, i) => (
              <img 
                key={i} 
                src={img.url} 
                className={`thumb ${mainImage === img.url ? 'active' : ''}`}
                onClick={() => setMainImage(img.url)}
                alt="thumbnail"
              />
            ))}
          </div>
          <div className="main-image-frame">
            <img src={mainImage} alt={product.name} />
          </div>
        </div>

        <div className="product-details">
          <h1 className="p-title">{product.name}</h1>
          
          <div className="p-price">
            <AnimatedPrice price={currentPrice.value} currency={currentPrice.currency} />
          </div>

          <div className="p-description" dangerouslySetInnerHTML={{ __html: product.description }} />

          <div className="size-selector">
            <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px'}}>
              <span className="label" style={{margin: 0}}>SELECT SIZE: {cleanVariantName(selectedVariant)}</span>
              {/* 👇 TRIGGER LINK */}
              <span 
                style={{fontSize: '12px', textDecoration: 'underline', cursor: 'pointer', fontWeight: '700', color: 'white'}}
                onClick={() => setIsSizeGuideOpen(true)}
              >
                Size guide
              </span>
            </div>
            
            <div className="size-grid">
              {product.variants.map(v => (
                <button
                  key={v.id}
                  className={`size-btn ${selectedVariant?.id === v.id ? 'active' : ''}`}
                  onClick={() => setSelectedVariant(v)}
                >
                  {cleanVariantName(v)}
                </button>
              ))}
            </div>
          </div>

          <div className="cart-actions">
            <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
            
            <button className="btn add-cart-btn" onClick={handleAdd}>
               ADD TO CART - <AnimatedPrice price={(currentPrice.value * quantity).toFixed(2)} currency={currentPrice.currency} />
            </button>
          </div>

          <div className="accordions">
            {product.sections && product.sections.map((sec) => {
              const isOpen = openSection === sec.title;
              return (
                <div className="accordion-item" key={sec.title}>
                  <button className={`acc-header ${isOpen ? 'active' : ''}`} onClick={() => toggle(sec.title)}>
                    <span>{sec.title}</span>
                    {isOpen ? <Minus size={16}/> : <Plus size={16}/>}
                  </button>
                  <div className={`acc-content-wrapper ${isOpen ? 'open' : ''}`}>
                    <div className="acc-inner">
                      <div className="acc-content" dangerouslySetInnerHTML={{ __html: sec.content_html }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}